/**
 * PODZIAŁ TYPÓW WNIOSKÓW: ZARZĄDCZY (o pracowniku) vs OSOBISTY (o sobie).
 *
 * [zgłoszenie właściciela 2026-09-02] „w /moj-dzial/wnioski jak klikam «Wniosek w swoim imieniu»,
 * a potem wybieram wnioski przełożonego, to nie mogę wybrać pracownika z działu, tylko tak jakbym
 * pisał sam o sobie".
 *
 * SKĄD SIĘ TO BRAŁO: lista „co mogę złożyć" (serwer, `GET /hr/available-types`) przepuszcza
 * przełożonemu także typy „tylko przełożony" — bramka rangi jest spełniona, więc formalnie MOŻE je
 * złożyć. Kreator WŁASNY (`HrNewRequestDrawer`) brał tę listę w całości, a nie ma kroku wyboru
 * pracownika — więc ocenę pracowniczą renderował jak urlop: bez podmiotu, jakby dotyczyła składającego.
 *
 * ZASADA: typ zarządczy składa się WYŁĄCZNIE kreatorem „w imieniu pracownika" (`ManagerRequestDrawer`),
 * który zaczyna od kroku „Pracownik" i niesie zakres działu. Kreator własny i sekcja „wnioski osobiste"
 * takich typów nie pokazują wcale; jeśli mimo to taki typ tam trafi (skrót, stary link), kreator własny
 * przekazuje go dalej albo odmawia — nigdy nie składa.
 *
 * Kryterium jest PODWÓJNE i to celowo: `requiresTargetEmployee` (dotyczy wskazanego pracownika) LUB
 * `managerOnly` (np. wniosek o zatrudnienie — zarządczy, choć bez podmiotu). Sam pierwszy warunek
 * zostawiłby wniosek o zatrudnienie w sekcji „dotyczące mnie".
 */
export const czyTypZarzadczy = (typ) =>
    !!typ && (typ.managerOnly === true || typ.requiresTargetEmployee === true);

/**
 * Kategorie z typami zawężone do OSOBISTYCH. Kategoria, w której nie zostaje nic (np. „Wnioski
 * przełożonego"), znika — pusta sekcja w kreatorze byłaby drzwiami donikąd.
 */
/**
 * [DELEGACJE 2026-09-12] TYP SYSTEMOWY — wniosek, który składa SYSTEM, nie człowiek (rozliczenie delegacji powstaje
 * z ekranu „Do rozliczenia" po policzeniu diet i salda). W kreatorze wniosków go nie ma: goły formularz bez
 * wyliczenia byłby dokumentem księgowym z pustymi kwotami. Znacznik siedzi w `availableFor.systemOnly`
 * (serwer go nie zna i nie blokuje — o dostępności decyduje jak dotąd).
 */
export const czyTypSystemowy = (typ) => {
    if (!typ) return false;
    if (typ.availableFor && typeof typ.availableFor === "object" && typ.availableFor.systemOnly === true) return true;
    if (typeof typ.availableForJson === "string" && typ.availableForJson.includes("\"systemOnly\":true")) return true;
    return false;
};

/**
 * [R36, polecenie właściciela 2026-09-13] „W sekcji /moj-dzial/wnioski nie widzę opcji dla delegacji, a prosiłem by była.”
 *
 * ROOT CAUSE: kafelki „Wnioski zarządcze” i kreator „w imieniu pracownika” brały WYŁĄCZNIE typy `managerOnly`, a polecenie
 * wyjazdu służbowego jest typem zwykłym (pracownik składa je też sam, więc `managerOnly` go by zablokował). Przełożony nie
 * miał jak wydać polecenia podwładnemu — choć polecenie wyjazdu z natury wydaje PRACODAWCA.
 *
 * Typ jest „do złożenia w imieniu pracownika”, gdy: `managerOnly`, ALBO po jego zatwierdzeniu powstaje obowiązek rozliczenia
 * (`wymagaRozliczenia` — delegacje), ALBO kadry jawnie oznaczyły `availableFor.przelozonyWImieniuPracownika`.
 * Typ systemowy (wniosek rozliczeniowy) — nigdy: powstaje wyłącznie z ekranu „Do rozliczenia”.
 */
export const czyTypWImieniuPracownika = (typ) =>
    !!typ && !czyTypSystemowy(typ) && (
        typ.managerOnly === true
        || typ.wymagaRozliczenia === true
        || (!!typ.availableFor && typeof typ.availableFor === "object" && typ.availableFor.przelozonyWImieniuPracownika === true)
    );

export const tylkoTypyOsobiste = (grupy) =>
    (grupy || [])
        .map((g) => ({ ...g, types: (g.types || []).filter((t) => !czyTypZarzadczy(t) && !czyTypSystemowy(t)) }))
        .filter((g) => g.types.length > 0);
